import React, { useEffect } from "react";
import { Card } from "../../Components/card";
import {
  useGetStudentdSchoolFees,
  useUpdateStudentSchoolFees,
} from "../../hooks/studentsSchoolFees/studentSchoolFees.hooks";
import InputField, { InputType } from "../../Components/input/inputField";
import { useDispatch } from "react-redux";
import { SectionHeader } from "../../Components/Header/SectionHeader";
import { BtnType, Button } from "../../Components/Button";
import {
  setErrorMessage,
  setSuccessMessage,
} from "../../Redux/Slices/alert.slice";
import { parseApiError } from "../../Helper/apiError.helper";
import { LunchPackagesOptions } from "../../Components/schoolPrices/lunchPackages/LunchPackagesOptions";
import { getValue } from "../../utils/object.utils";
import { DropdownOptions } from "../../Components/dropdownOptions";

interface IProps {
  studentId: number;
}

export const StudentSchoolFees = ({ studentId }: IProps) => {
  const dispatch = useDispatch();

  const [admissionFee, setAdmissionFee] = React.useState<number>(0);
  const [annualFee, setAnnualFee] = React.useState<number>(0);
  const [extraClassFee, setExtraClassFee] = React.useState<number>(0);
  const [monthlyFee, setMonthlyFee] = React.useState<number>(0);
  const [stationeryFee, setStationeryFee] = React.useState<number>(0);
  const [transportationFee, setTransportationFee] = React.useState<number>(0);
  const [examFee, setExamFee] = React.useState<number>(0);
  const [extraCirculumFee, setExtraCirculumFee] = React.useState<number>(0);
  const [lunchFee, setLunchFee] = React.useState<number>(0);
  const [lunchPackageId, setLunchPackagesId] = React.useState<number | null>(
    null
  );

  const { isLoading, data } = useGetStudentdSchoolFees({
    studentId,
  });

  useEffect(() => {
    if (!isLoading && data) {
      setAdmissionFee(data?.schoolFees?.admission_fee);
      setAnnualFee(data?.schoolFees?.annual_fee);
      setExamFee(data?.schoolFees?.exam_fee);
      setExtraCirculumFee(data?.schoolFees?.extra_carricular_fee);
      setExtraClassFee(data?.schoolFees?.extra_class_fee);
      setLunchFee(data?.schoolFees?.lunch_fee);
      setMonthlyFee(data?.schoolFees?.monthly_fee);
      setStationeryFee(data?.schoolFees?.stationery_fee);
      setTransportationFee(data?.schoolFees?.transportation_fee);
      setLunchPackagesId(data?.lunchPackage?.id || null);
      setLunchFee(data?.lunchPackage?.rate);
    }
  }, [isLoading, data]);

  const { isLoading: isUpdating, mutate: updateStudentSchoolFees } =
    useUpdateStudentSchoolFees({
      studentId,
      onSuccess: () => {
        dispatch(setSuccessMessage("Student school fees updated successfully"));
      },
      onError: (error) => {
        dispatch(setErrorMessage(parseApiError(error)));
      },
    });

  const handleUpdate = () => {
    updateStudentSchoolFees({
      admissionFee,
      annualFee,
      extraClassFee,
      monthlyFee,
      stationeryFee,
      transportationFee,
      examFee,
      extraCirculumFee,
      lunchPackageId,
    });
  };

  const handleLunchPackageSelect = (selectedLunchPackage: DropdownOptions) => {
    const lunchPackageId = getValue(selectedLunchPackage, "value");
    const lunchFee = getValue(selectedLunchPackage, "metaData.rate", 0);

    setLunchPackagesId(lunchPackageId);
    setLunchFee(lunchFee);
  };

  return (
    <Card>
      <SectionHeader headerText="Student school fees" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 mt-4">
        <InputField
          value={admissionFee}
          setValue={(value) => {
            setAdmissionFee(value);
          }}
          label="Admission fee*"
          name="update-student-admission-fee"
          isRequiredFocusonValidation
          type={InputType.Number}
        />
        <InputField
          value={annualFee}
          setValue={(value) => {
            setAnnualFee(value);
          }}
          label="Annual fee*"
          name="update-student-annual-fee"
          isRequiredFocusonValidation
          type={InputType.Number}
        />
        <InputField
          value={extraClassFee}
          setValue={(value) => {
            setExtraClassFee(value);
          }}
          label="Extra class fee *"
          name="update-student-extra-class-fee"
          isRequiredFocusonValidation
          type={InputType.Number}
        />
        <InputField
          value={monthlyFee}
          setValue={(value) => {
            setMonthlyFee(value);
          }}
          label="Monthly fee*"
          name="update-student-monthly-fee"
          isRequiredFocusonValidation
          type={InputType.Number}
        />
        <InputField
          value={stationeryFee}
          setValue={(value) => {
            setStationeryFee(value);
          }}
          label="Stationery fee*"
          name="update-student-stationerya-fee"
          isRequiredFocusonValidation
          type={InputType.Number}
        />
        <InputField
          value={transportationFee}
          setValue={(value) => {
            setTransportationFee(value);
          }}
          label="Transportation fee*"
          name="update-student-transportation-fee"
          isRequiredFocusonValidation
          type={InputType.Number}
        />
        <InputField
          value={examFee}
          setValue={(value) => {
            setExamFee(value);
          }}
          label="Exam fee*"
          name="update-student-exam-fee"
          isRequiredFocusonValidation
          type={InputType.Number}
        />
        <InputField
          value={extraCirculumFee}
          setValue={(value) => {
            setExtraCirculumFee(value);
          }}
          label="Extra cirriculam fee*"
          name="update-student-extra-cirriculam-fee"
          isRequiredFocusonValidation
          type={InputType.Number}
        />
        <LunchPackagesOptions
          onLunchPackageSelect={handleLunchPackageSelect}
          selectedLunchPackageId={lunchPackageId}
        />
        <InputField
          value={lunchFee}
          setValue={(value) => {}}
          label="Lunch fee"
          name="update-student-lunch-fee"
          isRequiredFocusonValidation
          type={InputType.Number}
          disabled
        />
      </div>

      <div className="mt-12">
        <Button
          btnType={BtnType.primary}
          text="Save"
          loading={isUpdating}
          onClick={handleUpdate}
        />
      </div>
    </Card>
  );
};
